import { useMemo, useState } from "react";
import { motion } from "framer-motion";
import { formatLocalDate } from "../../utils/formatDate";
import { formatarMoedaBrasileira } from "../../utils/formatarMoedaBrasileira";
import { CustomTable } from "../../componentes/table/CustomTable";
import Button from "../../componentes/button/button";
import { Dialog } from "primereact/dialog";
import CadastroAjusteDL from "../comissoes-motoristas/ajustes/CadastroAjusteDL";

const fmtDate = (v) => formatLocalDate(v);
const fmtNum = (v, d = 2) => v != null ? Number(v).toLocaleString("pt-BR", { minimumFractionDigits: d, maximumFractionDigits: d }) : "—";
const fmtBRL = (v) => v != null ? `R$ ${formatarMoedaBrasileira(Number(v))}` : "—";

function StatCard({ icon, label, value, color = "teal" }) {
    const colors = {
        teal: "bg-teal-50   border-teal-200   text-teal-700",
        blue: "bg-blue-50   border-blue-200   text-blue-700",
        violet: "bg-violet-50 border-violet-200 text-violet-700",
        amber: "bg-amber-50  border-amber-200  text-amber-700",
        orange: "bg-orange-50 border-orange-200 text-orange-700",
        rose: "bg-rose-50   border-rose-200   text-rose-700",
        green: "bg-green-50  border-green-200  text-green-700",
        gray: "bg-muted border-border text-foreground",
    };
    return (
        <div className={`rounded-xl border p-3 flex items-center gap-3 ${colors[color]}`}>
            <i className={`${icon} text-lg w-5 text-center flex-shrink-0`} />
            <div className="min-w-0">
                <p className="text-[10px] opacity-70 leading-tight truncate">{label}</p>
                <p className="text-sm font-bold leading-tight truncate">{value}</p>
            </div>
        </div>
    );
}

function AjusteBadge({ value }) {

    if (!value) {
        return <span className="text-muted-foreground text-[10px]">—</span>;
    }

    const map = {
        APROVADO: { label: "Ajustado Manualmente", cls: "bg-success/10 text-success" },
        REJEITADO: { label: "Rejeitado", cls: "bg-destructive/10 text-destructive" },
        PENDENTE: { label: "Pendente", cls: "bg-yellow-100 text-yellow-700" },
    };

    const cfg = map[value] ?? {
        label: value,
        cls: "bg-muted text-muted-foreground",
    };

    const isForaPrazo = value === "FORA_DO_PRAZO";

    // 👉 Caso normal (badge)
    return (
        <span
            className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-semibold ${cfg.cls} ${isForaPrazo ? "cursor-pointer" : ""}`}
        >
            {cfg.label}
        </span>
    );
}

function SolicitarAjuste({ value, onClickForaPrazo }) {

    if (value) {
        return <span className="text-muted-foreground text-[10px]">—</span>;
    }

    // 👉 Caso normal (badge)
    return (
        <span className="cursor-pointer inline-block px-2 py-0.5 rounded-full text-[10px] font-semibold bg-primary/10 text-primary transition-colors duration-150 hover:bg-primary/20" onClick={onClickForaPrazo}>Solic. Ajuste</span>
    );
}

export default function TabelaRetornosSap({ data = [], loading = false }) {
    const [showDialog, setShowDialog] = useState(false);
    const [ajusteData, setAjusteData] = useState(null);

    const stats = useMemo(() => {
        if (!data.length) return null;
        const totalCxf = data.reduce((a, r) => a + (Number(r.caixasFisicas) || 0), 0);
        const totalCxu = data.reduce((a, r) => a + (Number(r.caixasUnitarias) || 0), 0);
        const totalCubos = data.reduce((a, r) => a + (Number(r.cubos) || 0), 0);
        const totalValor = data.reduce((a, r) => a + (Number(r.valorTotal) || 0), 0);
        const clientes = new Set(data.map(r => r.clienteId)).size;
        const motivos = new Set(data.map(r => r.motivoCodigo).filter(Boolean)).size;
        const notas = new Set(data.map(r => r.notaFiscal).filter(Boolean)).size;
        const produtos = new Set(data.map(r => r.produtoId).filter(Boolean)).size;
        return { total: data.length, totalCxf, totalCxu, totalCubos, totalValor, clientes, motivos, produtos, notas };
    }, [data]);

    const columns = useMemo(() => [
        { key: "filialNome", label: "Filial", sortable: true },
        {
            key: "escalaVinculada",
            label: "Escala Vinc.",
            sortable: true,
            render: (item) => <span className={`px-1 py-1 font-semibold rounded-lg text-[10px] text-white ${item ? 'bg-success' : 'bg-yellow-500'}`}>{item ? "Vinculada" : "Não Vinc."}</span>
        },
        { key: "carga", label: "Carga", sortable: true, className: "font-semibold" },
        { key: "transporte", label: "Transporte", sortable: true, center: true },
        { key: "canal", label: "Canal", sortable: true, center: true },
        {
            key: "dataEntrega",
            label: "Dt. Entrega",
            sortable: true,
            center: true,
            render: (v) => fmtDate(v),
        },
        {
            key: "dataRetorno",
            label: "Dt. Retorno",
            sortable: true,
            center: true,
            render: (v) => fmtDate(v),
        },
        {
            key: "dataCriacao",
            label: "Dt. Criação",
            sortable: true,
            center: true,
            render: (v) => fmtDate(v),
        },
        // ── Cliente ────────────────────────────────────────────────────────
        {
            key: "clienteCodigo",
            label: "Cód. Cliente",
            sortable: true,
            center: true,
            className: "bg-primary/5",
        },
        {
            key: "clienteRazaoSocial",
            label: "Razão Social",
            sortable: true,
            className: "bg-primary/5",
        },
        {
            key: "clienteFantasia",
            label: "Fantasia",
            sortable: true,
            className: "bg-primary/5",
        },
        // ── Produto ────────────────────────────────────────────────────────
        {
            key: "produtoNome",
            label: "Produto",
            sortable: true,
            className: "bg-violet-50 font-semibold",
        },
        {
            key: "produtoCategoria",
            label: "Categoria",
            sortable: true,
            className: "bg-violet-50",
        },
        {
            key: "produtoSabor",
            label: "Sabor",
            sortable: true,
            className: "bg-violet-50",
        },
        // ── Motivo ────────────────────────────────────────────────────────
        {
            key: "motivoCodigo",
            label: "Cód. Motivo",
            sortable: true,
            center: true,
            className: "bg-rose-50",
        },
        {
            key: "motivoNome",
            label: "Motivo",
            sortable: true,
            className: "bg-rose-50 font-semibold",
        },
        // ── NF / Vendedor ─────────────────────────────────────────────────
        {
            key: "notaFiscal",
            label: "Nota Fiscal",
            sortable: true,
            center: true,
            className: "bg-muted",
        },
        {
            key: "codigoVendedor",
            label: "Vendedor",
            sortable: true,
            center: true,
            className: "bg-muted",
        },
        {
            key: "motoristaCodigo",
            label: "Motorista",
            sortable: true,
            center: true,
        },
        {
            key: "veiculoCodigo",
            label: "Veículo",
            sortable: true,
            center: true,
        },
        // ── Volumes ───────────────────────────────────────────────────────
        {
            key: "caixasFisicas",
            label: "Cx. Físicas",
            sortable: true,
            center: true,
            className: "bg-amber-50 font-semibold",
            render: (v) => fmtNum(v),
        },
        {
            key: "caixasUnitarias",
            label: "Cx. Unitárias",
            sortable: true,
            center: true,
            className: "bg-amber-50 font-semibold",
            render: (v) => fmtNum(v),
        },
        {
            key: "cubos",
            label: "Cubos",
            sortable: true,
            center: true,
            className: "bg-teal-50 font-semibold",
            render: (v) => fmtNum(v),
        },
        {
            key: "valorTotal",
            label: "Valor Total",
            sortable: true,
            center: true,
            className: "bg-green-50 font-semibold",
            render: (v) => fmtBRL(v),
        },
        {
            key: "ajusteComissaoStatus",
            label: "Ajuste",
            sortable: true,
            center: true,
            render: (i) => <AjusteBadge value={i} />,
        },
        {
            key: "entregueNoPrazo",
            label: "Ação",
            sortable: true,
            center: true,
            render: (_, row) => (
                <SolicitarAjuste
                    value={row.ajusteComissaoId}
                    onClickForaPrazo={() => {
                        setAjusteData({
                            filialId: row.filialId,
                            transporte: row.transporte,
                            tipo: "IGNORAR_RETORNO",
                            clienteId: row.clienteId
                        });
                        setShowDialog(true);
                    }}
                />
            ),
        },
    ], []);

    return (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="bg-background">

            {/* KPIs */}
            {stats && (
                <div className="bg-card border-b border-border px-4 py-3">
                    <div className="grid grid-cols-2 sm:grid-cols-4 xl:grid-cols-6 gap-2">
                        <StatCard icon="fa fa-undo" label="Retornos" value={stats.total.toLocaleString("pt-BR")} color="rose" />
                        <StatCard icon="fa fa-users" label="Clientes / NF's / Motivos" value={stats.clientes + " / " + stats.notas + " / " + stats.motivos} color="blue" />
                        <StatCard icon="fa fa-cube" label="Cubos" value={fmtNum(stats.totalCubos)} color="teal" />
                        <StatCard icon="fa fa-box" label="Cx. Físicas" value={fmtNum(stats.totalCxf)} color="amber" />
                        <StatCard icon="fa fa-cubes" label="Cx. Unitárias" value={fmtNum(stats.totalCxu)} color="violet" />
                        <StatCard icon="fa fa-dollar" label="Valor Total" value={fmtBRL(stats.totalValor)} color="green" />
                    </div>
                </div>
            )}

            <CustomTable
                data={data}
                columns={columns}
                showHeader={false}
                loading={loading}
                maxHeight="calc(100vh - 250px)"
                exportFilename="Base de Retornos"
                exportable
                pagination
                pageSize={50}
                className="text-[11px]"
                footer={(rows) => {
                    const totalCxf = rows.reduce((a, r) => a + (Number(r.caixasFisicas) || 0), 0);
                    const totalCxu = rows.reduce((a, r) => a + (Number(r.caixasUnitarias) || 0), 0);
                    const totalCubos = rows.reduce((a, r) => a + (Number(r.cubos) || 0), 0);
                    const totalValor = rows.reduce((a, r) => a + (Number(r.valorTotal) || 0), 0);
                    return (
                        <div className="flex flex-wrap justify-end gap-5 p-2 text-xs font-semibold text-foreground">
                            <span>Registros: {rows.length}</span>
                            <span className="text-teal-700">Cubos: {fmtNum(totalCubos)}</span>
                            <span className="text-amber-600">Cx. Fís.: {fmtNum(totalCxf)} · Cx. Unit.: {fmtNum(totalCxu)}</span>
                            <span className="text-green-700">Valor: {fmtBRL(totalValor)}</span>
                        </div>
                    );
                }}
            />

            {showDialog && (
                <Dialog
                    header="Solicitar ajuste de comissão"
                    visible={showDialog}
                    onHide={() => setShowDialog(false)}
                    className="w-[80%]"
                >
                    <CadastroAjusteDL
                        defaultValues={ajusteData}
                        showFilial={false}
                        showTransporte={false}
                        onClose={() => setShowDialog(false)}
                        onSuccess={() => {
                            setShowDialog(false);
                            // opcional: reload
                        }}
                    />
                </Dialog>
            )}
        </motion.div>
    );
}
